import { Box, Center, Flex, Input, Loader, Modal, Text } from '@mantine/core';
import { useGitbookAsk, useGitbookRecommendedQuestions } from './chat.api';
import { useState } from 'react';
import MessageCard from './MessageCard';
import VoiceCommand from './VoiceCommand';
import useTrackingChat from './chat.analytics';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../hooks';
import { Tooltip } from '@mantine/core';
import { BetaIcon } from '../../../lib/components';

import classes from './Chat.module.css';
import RecommendedList from './RecommendedList';

export const DOCUMENTATION_URL = 'https://docs.live-eo.com/tradeaware';

const Chat = ({ opened, close }: { opened: boolean; close: () => void }) => {
  const [question, setQuestion] = useState('');
  const { t } = useTranslation();
  const { data: user } = useUser();
  const [messages, setMessages] = useState<string[]>([
    t('chat.startMessage', { name: user?.firstName }),
  ]);
  const [showRecommendedQuestions, setShowRecommendedQuestions] =
    useState(false);

  const { data: recommendedQuestions } = useGitbookRecommendedQuestions({
    enabled: showRecommendedQuestions,
  });
  const { mutate: askQuestion, isLoading, data: answer } = useGitbookAsk();
  const { track } = useTrackingChat();

  const handleCloseChat = () => {
    track('Chat Ended', {
      questions_asked: (messages.length - 1) / 2,
    });
    close();
  };

  const handleAskQuestion = (
    question: string,
    type: 'text' | 'voice' | 'recommended'
  ) => {
    track('Question Asked', {
      question,
      type,
    });

    setMessages([...messages, question]);

    askQuestion(question, {
      onSuccess: (data) => {
        track('Answer Received', {
          question,
          answer: data.answer,
        });

        setMessages((prev) => [...prev, data.answer]);
        setQuestion('');
      },
    });
  };

  const showDocumentation = (source: 'chat-header' | 'first-step') => {
    track('Chat Link Clicked', {
      link_url: DOCUMENTATION_URL,
      source,
    });
    window.open(DOCUMENTATION_URL, '_blank');
  };

  const recommendedChatQuestions =
    (answer?.followupQuestions || recommendedQuestions)?.map((question) => ({
      label: question,
      key: question,
      onClick: () => handleAskQuestion(question, 'recommended'),
    })) ?? [];

  const showDefaultRecommendedItems =
    !showRecommendedQuestions && messages.length === 1;

  const defaultRecommendedItems = [
    {
      label: t('chat.helpByChat'),
      key: 'chat',
      onClick: () => setShowRecommendedQuestions(true),
    },
    {
      label: t('chat.showDocumentation'),
      key: 'docs',
      onClick: () => showDocumentation('first-step'),
    },
  ];

  return (
    <Modal
      closeOnClickOutside={false}
      withOverlay={false}
      lockScroll={false}
      size={580}
      radius="8px 8px 0 0"
      opened={opened}
      onClose={handleCloseChat}
      title={
        <Flex justify="space-between" w="99%" align="center">
          <Tooltip
            label={t('chat.infoTooltip')}
            multiline
            w={300}
            transitionProps={{ transition: 'fade', duration: 300 }}
          >
            <Flex gap={8} align="center">
              <Text size="xl">{t('chat.title')}</Text>
              <BetaIcon icon="circle-info" size="sm" />
            </Flex>
          </Tooltip>

          <BetaIcon
            icon="help-circle"
            size="sm"
            cursor="pointer"
            onClick={() => showDocumentation('chat-header')}
          />
        </Flex>
      }
      classNames={{
        header: classes.header,
        title: classes.title,
        inner: classes.inner,
        content: classes.content,
        body: classes.body,
      }}
      transitionProps={{ transition: 'slide-up' }}
    >
      <Box mih="378px">
        {messages.map((message, index) => (
          <MessageCard
            key={message + index}
            replies={[message]}
            isUser={index % 2 === 0}
          />
        ))}

        {showDefaultRecommendedItems && (
          <RecommendedList items={defaultRecommendedItems} />
        )}

        {isLoading ? (
          <Center>
            <Loader type="dots" color="dark.6" />
          </Center>
        ) : (
          <>
            {showRecommendedQuestions && (
              <RecommendedList items={recommendedChatQuestions} />
            )}
          </>
        )}
      </Box>

      <Box className={classes.inputWrapper}>
        <Input
          disabled={isLoading}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={t('chat.placeholder')}
          maxLength={512}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAskQuestion(question, 'text');
            }
          }}
          styles={{
            section: {
              pointerEvents: 'auto',
            },
          }}
          rightSection={
            <VoiceCommand
              handleAskQuestion={handleAskQuestion}
              question={question}
              isLoading={isLoading}
            />
          }
        />
      </Box>
    </Modal>
  );
};

export default Chat;
